import { useEffect, useRef, useState } from 'react';
import type { MediaRole } from '../../../shared/types';
import { DAILY_NAME } from '../../../shared/constants';
import type { TodayState } from '../hooks';
import { enqueueUpload } from '../uploadQueue';
import { IconClose, IconSelfie } from '../ui';
import { goBack, swap } from '../router';
import { useDecorEditor } from '../DecorEditor';
import { markAnswered } from '../giviSnapLock';
import { PhotoBackdrop } from '../SnapDecor';

interface Props {
  today: TodayState;
  windowEndsAt: number | null;
  /** When this person's GiviSnap went up, if it already has. */
  postedAt: number | null;
}

interface Shot {
  file: File;
  preview: string;
}

/**
 * The first shot of a GiviSnap, taken from the snap sheet and waiting here.
 *
 * The sheet opens the back camera itself, because a camera only opens from
 * inside a tap and the tap is on the sheet. What comes back is handed over
 * through this rather than the route, which carries words, not files.
 */
let handedOver: Shot | null = null;

export function startGiviSnap(file: File): void {
  if (handedOver) URL.revokeObjectURL(handedOver.preview);
  handedOver = { file, preview: URL.createObjectURL(file) };
}

/**
 * How long is left, ticking on its own.
 *
 * Its own component for one reason: a second passing used to set state on the
 * whole camera screen, which re-rendered the roster, the rail and every story
 * circle once a second for as long as the window was open. Here, a tick
 * repaints five characters.
 *
 * That waste was cheap to miss because React bails out of a re-render when the
 * value has not changed -- so outside the window, where the number sits at
 * nought, it cost nothing and looked fine. Inside it, which is the one time
 * anybody is hurrying, it was re-rendering the busiest screen in the app every
 * second.
 */
function Countdown({ endsAt }: { endsAt: number }) {
  const [left, setLeft] = useState(() =>
    Math.max(0, Math.floor((endsAt - Date.now()) / 1000)),
  );

  useEffect(() => {
    const tick = () => setLeft(Math.max(0, Math.floor((endsAt - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [endsAt]);

  return (
    <span className="capture-clock">
      {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')} left
    </span>
  );
}

/**
 * The GiviSnap, after its first shot: the selfie, the words and emojis, and
 * the post.
 *
 * The only way here is the snap sheet's GiviSnap tile. Arriving with nothing
 * in hand -- a stale link, the back button -- there is nothing to show, so it
 * goes straight back to the chat.
 */
export function Camera({ today, windowEndsAt, postedAt }: Props) {
  // The day being answered, not the calendar one. A GiviSnap taken before
  // today's prompt fires answers last night's, so it belongs in that day
  // alongside the others — written to the calendar day it would count for the
  // right streak and then not appear on the screen at all.
  const day = today.showing;

  // Read once, not taken: StrictMode runs this twice and would lose it.
  const [back, setBack] = useState<Shot | null>(() => handedOver);
  const [front, setFront] = useState<Shot | null>(null);
  // Written on the way a snap is: tap the picture, pinch anywhere.
  const editor = useDecorEditor();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    handedOver = null;
  }, []);

  useEffect(() => {
    if (!back) goBack('chat');
  }, [back]);


  const frontInput = useRef<HTMLInputElement>(null);
  const frontLibraryInput = useRef<HTMLInputElement>(null);


  // Object URLs are a leak if the tab is left without posting. Each shot's own,
  // let go when that shot is replaced or the screen goes: let go together, the
  // selfie arriving or a retake took the back photo out from under itself
  // while it was still on screen.
  useEffect(() => (back ? () => URL.revokeObjectURL(back.preview) : undefined), [back]);
  useEffect(() => (front ? () => URL.revokeObjectURL(front.preview) : undefined), [front]);

  const pick = (role: MediaRole, file: File | undefined) => {
    if (!file) return;

    // The chooser that reaches the camera app has no `accept` to narrow it, so
    // it can hand back a document as easily as a photograph.
    if (!file.type.startsWith('image/')) {
      setError('That is not a photo. Pick a picture, or use the camera.');
      return;
    }

    const shot = { file, preview: URL.createObjectURL(file) };
    if (role === 'front') setFront(shot);
    else setBack(shot);
    // The front camera cannot be opened from here: iOS ignores a file input
    // opened without a fresh gesture. So the back shot hands over to a step
    // that asks for the tap, rather than dropping you back where you started
    // with no idea that the job is half done.
  };

  /** Drop what is in hand and go back to looking at everyone else's. */
  const reset = () => {
    setBack(null);
    setFront(null);
    setError(null);
  };


  const onPost = () => {
    if (!back || !front) return;
    setError(null);

    // Data, not a closure: the queue writes this to IndexedDB so the bytes
    // survive the app closing and finish on the next launch.
    enqueueUpload({
      kind: 'daily',
      day,
      // Names this one so both cameras land in the same post, and so a second
      // today becomes its own rather than overwriting the first.
      postId: crypto.randomUUID(),
      back: back.file,
      front: front.file,
      ...editor.decor,
    });
    // Everyone else's open up now, not when the upload lands.
    markAnswered(day);

    setBack(null);
    setFront(null);
    // This screen no longer shows anybody's pictures, so it goes to the one
    // that shows yours.
    swap('today');
  };

  /* Whether there is a clock to show at all. What it says is the clock's own
     business, and it ticks inside itself. */
  const counting = Boolean(windowEndsAt) && !postedAt;


  /*
   * Mounted once and never moved, whichever half of the screen is showing.
   * A file input that unmounts between steps loses its ref, and the tap that
   * was meant to open the front camera then does nothing at all.
   */
  const inputs = (
    <>
      {/*
        `capture`, and back for good.

        The chooser it replaced was an attempt to reach the camera app, on the
        belief that Google Camera would then write what it writes for itself.
        It does not. A photograph taken in the camera app carries an Ultra HDR
        gain map -- three XMP segments, an APP11 block, fourteen kilobytes of
        Exif -- and one taken through any web route carries none of it, the
        same camera app answering an intent included. Measured, not guessed:
        two photographs off the same phone, identical quantisation tables, one
        with a gain map and one without.

        So there is nothing to be won at the shutter and a tap to be lost on
        every picture. `capture` at least asks for the right lens, which is
        also what makes the selfie step open facing the right way.

        The way to a good selfie on a Pixel is to take it in the camera app
        and bring it in -- the button under the selfie shutter, or Library on
        the rail. Those files arrive with the gain map intact.
      */}
      <input
        ref={frontInput}
        type="file"
        accept="image/*"
        capture="user"
        className="sr-only"
        onChange={(e) => pick('front', e.target.files?.[0])}
      />
      {/*
        The two ways in to a photograph already taken, which on a Pixel is the
        only way to a selfie with the camera's own processing on it. `accept`
        is set so these get the system photo picker rather than the older
        chooser: the camera apps listed in that one turned out to hand back the
        same flat file as the shutter, so there is no reason to reach them.
      */}
      <input
        ref={frontLibraryInput}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => pick('front', e.target.files?.[0])}
      />
    </>
  );

  if (back) {
    return (
      <>
        {inputs}
        <div className="snap-step">
          {/* As the viewer will show it: its own colours blurred round it. */}
          <PhotoBackdrop src={back.preview} />
          <img src={back.preview} alt="" className="snap-step-shot" />
          {front && <img src={front.preview} alt="" className="snap-step-inset" />}

          {/*
            * Over the picture, where it will be: a tap on it writes, a pinch
            * anywhere sizes what was touched last. Under the selfie and the
            * controls, so their own taps stay theirs.
            */}
          {front && (
            <div className="snap-step-surface" {...editor.surface}>
              {editor.layer}
            </div>
          )}

          <div className="snap-step-top">
            <button className="icon-btn on-photo" aria-label="Start over" onClick={reset}>
              <IconClose size={17} />
            </button>
            <span className="grow" />
            {counting && <Countdown endsAt={windowEndsAt as number} />}
          </div>

          {front && <div className="snap-step-tools">{editor.tools}</div>}

          <div className="snap-step-foot">
            {!front ? (
              <>
                <span className="snap-step-title">Selfie time</span>
                <span className="snap-step-sub">
                  Now the other way round, before the moment goes.
                </span>
                <button
                  className="snap-step-go"
                  onClick={() => frontInput.current?.click()}
                >
                  <IconSelfie size={27} />
                </button>
                {/*
                  The camera app's own file is the only one with the Pixel's
                  processing on it, so this is not a convenience -- on that
                  phone it is the only route to a good selfie.
                */}
                <button
                  className="snap-step-alt"
                  onClick={() => frontLibraryInput.current?.click()}
                >
                  Use a photo from your camera app
                </button>
              </>
            ) : (
              <>
                {editor.blank && <span className="snap-step-hint">Tap to write on it</span>}
                <div className="row" style={{ gap: 10, width: '100%' }}>
                  <button className="btn btn--ghost" onClick={() => setFront(null)}>
                    Retake
                  </button>
                  <button className="btn grow" onClick={onPost}>
                    Post {DAILY_NAME}
                  </button>
                </div>
              </>
            )}
            {error && <span className="capture-hint">{error}</span>}
          </div>

          {front && editor.overlays}
        </div>
      </>
    );
  }

  return null;
}
